import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IdentityDocument } from '../../../../core/types/client-document';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { fileValidation } from '../../../../core/validation/file-validation';
import { IdCardImageTypes } from '@app/core/types/identity-verification';

@Component({
  selector: 'itfg-id-card-image-list',
  templateUrl: './id-card-image-list.component.html',
  styleUrls: ['./id-card-image-list.component.scss'],
  standalone: false
})
export class IdCardImageListComponent implements OnInit, OnChanges {
  @Input() selectedImageId: number;
  @Input() clientId: number;
  @Input() idCardList: IdentityDocument[];
  @Input() idCardImageType: IdCardImageTypes;
  @Input() uploadInProgress: boolean = false;
  @Output() imageChange = new EventEmitter<any>();
  @Output() imageSave = new EventEmitter<any>();
  @Output() imageDelete = new EventEmitter<any>();
  @Output() imageRotate = new EventEmitter<{ document: IdentityDocument, clockwise: boolean }>();
  @Output() imageDownload = new EventEmitter<IdentityDocument>();
  selectedImageControl = new FormControl();
  form: FormGroup = new FormGroup({
    documents: this.selectedImageControl,
  });
  idCardListData: IdentityDocument[];
  idCardListDataForm: UntypedFormGroup;
  public IdCardImageTypes: typeof IdCardImageTypes = IdCardImageTypes;
  public maxFileSize: number;

  constructor(public _viewContainerRef: ViewContainerRef,
    public _dialogService: DialogProviderService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService) {
    this.maxFileSize = 9; // 9MB
  }

  ngOnInit(): void {
    this.idCardListDataForm = this.createIdCardListDataForm();
    this.form.valueChanges.subscribe((formChanged) => {
      if (formChanged.documents?.length > 0) {
        const selectedDocument = formChanged.documents[0];
        this.changeImage(selectedDocument);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.idCardListData = this.idCardList;
    this.idCardListDataForm = this.createIdCardListDataForm();

    if (
      !!this.selectedImageId
    ) {
      this.selectedImageControl.patchValue([this.idCardListData.find(doc => doc.id === this.selectedImageId)], {
        emitEvent: false,
      })
    }

  }

  saveDocument() {
    this.imageSave.emit({
      documentType: this.idCardImageType,
      document: this.idCardListDataForm.value['newFile']
    });
  }

  changeImage(document) {
    this.imageChange.emit(document);
  }

  rotateImage(documentId, clockwise) {
    this.imageRotate.emit({
      document: this.idCardList.find(d => d.id === this.selectedImageId),
      clockwise
    });
  }

  deleteCurrentlySelectedDocument() {
    this.deleteImage(this.idCardList.find(doc => doc.id === this.selectedImageId));
  }

  downloadCurrentlySelectedImage(documentId: number) {
    this.imageDownload.emit(this.idCardList.find(doc => doc.id === this.selectedImageId));
  }

  deleteImage(document) {
    this.translateService
      .get(
        [
          'clients.deleteDocument',
          'global.confirm',
          'global.cancel',
          'global.delete',
        ],
        {
          document:
            document.name,
        }
      ).subscribe(translation => {
        const dialogConfig = {
          message: translation['clients.deleteDocument'],
          disableClose: false,
          viewContainerRef: this._viewContainerRef,
          title: translation['global.delete'],
          cancelButton: translation['global.cancel'],
          acceptButton: translation['global.confirm'],
        };
        this._dialogService
          .openConfirm(dialogConfig)
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) {
              this.imageDelete.emit(document);
            }
          });
      });

  }

  onFileSelected(file: File, control: UntypedFormControl) {
    control.patchValue(file);
    this.selectedImageControl.patchValue(null, {emitEvent: false});
    this.selectedImageId = null;
    this.saveDocument();
  }

  createIdCardListDataForm() {
    const fb = this.formBuilder;
    return fb.group({
      newFile: ['', [fileValidation.maxSize]]
    });
  }

  inputControl(event) {
    event.preventDefault();
  }

}
